// src/app/navUtils.js
//
// ROUTING-PHASE-1: Pure navigation utility functions extracted from App.jsx
// so they can be imported by both App.jsx (re-exported as the public API used
// by tests) and AppShell.jsx (used at runtime) without creating a circular
// import between the two.
//
// These are pure functions — no hooks, no side effects, no React imports.
import { governedNavEnabled } from "./governedNav.js";
// All remain unit-testable by importing from App.jsx (which re-exports them).

import { applyAllFilters, applyAllFiltersAndSort, applySearchFilter } from "../services/filterLocations.js";
import { applyColumnSort } from "../utils/sortLocationsColumn.js";
import { applyColumnSort as applyLeaseColumnSort } from "../utils/sortLeasesColumn.js";
import { applyLeaseSort } from "../features/leases/leasePortfolioSort.js";
import { applyFilters as applyLeaseFilters, DEFAULT_FILTERS as DEFAULT_LEASE_FILTERS } from "../features/leases/pages/components/LeasePortfolioFilters.jsx";
import { ADMIN_ACTIVE_SECTION_STORAGE_KEY } from "../features/admin/AdminPage.jsx";

// ─── View-enum constants ─────────────────────────────────────────────────────

export const VIEW_PULSE               = "home";
export const VIEW_OFFICES             = "portfolio";
export const VIEW_LEASES              = "leases";
export const VIEW_LOCATION_DETAIL     = "location-detail";
export const VIEW_REPORTS             = "reports-landing";
export const VIEW_REPORT_WPR          = "report-wpr";
export const VIEW_REPORT_HC_TREND     = "report-headcount-trend";
export const VIEW_REPORT_OPEN_ROLES   = "report-open-roles";
export const VIEW_REPORT_SPACE_FIT    = "report-space-fit";
export const VIEW_REPORT_COST_LEAKAGE = "report-cost-leakage";
export const VIEW_REPORT_RTO_COMPLIANCE = "report-rto-compliance";
export const VIEW_REPORT_FOOT_TRAFFIC = "report-foot-traffic";
export const VIEW_REPORT_UTILIZATION  = "report-utilization";
export const VIEW_REPORT_DATA_COVERAGE = "report-data-coverage";
export const VIEW_DATA_HEALTH         = "data_health";
export const VIEW_ADMIN               = "admin";
export const VIEW_IMPORTS_QUEUE       = "imports-queue";
export const VIEW_IMPORTS_SESSION     = "imports-session";
export const VIEW_PROJECTS            = "projects";
export const VIEW_PROJECT_DETAIL      = "project-detail";
export const VIEW_MARKET_PLAN         = "plan";
export const VIEW_ROADMAP             = "roadmap";
export const VIEW_WORKFLOWS           = "workflows";
export const VIEW_OKRS                = "okrs";
export const VIEW_RADAR               = "radar";
export const VIEW_INFOCUS_RADAR       = "infocus-radar";

export const VIEW_RADAR_DECISIONS     = "radar-decisions";
export const REPORT_VIEWS = new Set([
  VIEW_REPORTS, VIEW_REPORT_WPR, VIEW_REPORT_HC_TREND, VIEW_REPORT_OPEN_ROLES,
  VIEW_REPORT_SPACE_FIT, VIEW_REPORT_COST_LEAKAGE, VIEW_REPORT_RTO_COMPLIANCE,
  VIEW_REPORT_FOOT_TRAFFIC, VIEW_REPORT_UTILIZATION,
  VIEW_REPORT_DATA_COVERAGE,
]);

export const PAGE_LABELS = {
  [VIEW_PULSE]:               "Home",
  [VIEW_OFFICES]:             "Offices",
  [VIEW_LEASES]:              "Leases",
  [VIEW_REPORTS]:             "Reports",
  [VIEW_REPORT_WPR]:          "Reports",
  [VIEW_REPORT_HC_TREND]:     "Reports: Headcount Trend",
  [VIEW_REPORT_OPEN_ROLES]:   "Reports: Open Roles",
  [VIEW_REPORT_RTO_COMPLIANCE]: "Reports: RTO Compliance",
  [VIEW_REPORT_FOOT_TRAFFIC]: "Reports: Foot Traffic",
  [VIEW_REPORT_COST_LEAKAGE]: "Reports: Cost Leakage",
  [VIEW_REPORT_UTILIZATION]:   "Reports: Attendance & Utilization",
  [VIEW_REPORT_DATA_COVERAGE]: "Data coverage",
  [VIEW_DATA_HEALTH]:         "Data Health",
  [VIEW_ADMIN]:               "Admin",
  [VIEW_LOCATION_DETAIL]:     "Location",
  [VIEW_IMPORTS_QUEUE]:       "Pending Imports",
  [VIEW_IMPORTS_SESSION]:     "Import Review",
  [VIEW_PROJECTS]:            "Projects",
  [VIEW_PROJECT_DETAIL]:      "Project",
  [VIEW_MARKET_PLAN]:         "Market Plan",
  [VIEW_ROADMAP]:             "Roadmap",
  [VIEW_WORKFLOWS]:           "Actions",
  [VIEW_OKRS]:                "OKRs",
  [VIEW_RADAR]:               "Leadership Radar",
  // NAV-RESTRUCTURE-RADAR: InFocusRadarPage is now branded simply as "Radar".
  [VIEW_INFOCUS_RADAR]:       "Radar",
  [VIEW_RADAR_DECISIONS]:     "Radar: Decisions",
};

// ─── Pure functions ──────────────────────────────────────────────────────────

/**
 * Feature flag view guard — pure function used by AppShell's useEffect.
 * Returns VIEW_OFFICES whenever the supplied view is gated behind a flag
 * that is currently OFF; otherwise returns the view unchanged.
 *
 * Exported as a pure function so it can be unit-tested directly via App.jsx.
 */
export function applyViewGuard(view, flags, reportFlags, navConfig) {
  const navEnabled = (key) => governedNavEnabled(flags, navConfig, key);
  if (view === VIEW_PULSE         && !navEnabled("home"))             return VIEW_OFFICES;
  if (view === VIEW_DATA_HEALTH)                                       return VIEW_ADMIN;
  if ((view === VIEW_PROJECTS || view === VIEW_PROJECT_DETAIL) && !navEnabled("projects")) return VIEW_OFFICES;
  if (view === VIEW_MARKET_PLAN   && !navEnabled("places"))           return VIEW_OFFICES;
  if (REPORT_VIEWS.has(view)      && !navEnabled("reports"))          return VIEW_OFFICES;
  if (view === VIEW_LEASES        && !navEnabled("leases"))           return VIEW_OFFICES;
  if (view === VIEW_ROADMAP       && !navEnabled("roadmap"))          return VIEW_OFFICES;
  if (view === VIEW_WORKFLOWS     && !navEnabled("workflows"))        return VIEW_OFFICES;
  if (view === VIEW_OKRS          && !navEnabled("okrs"))             return VIEW_OFFICES;
  if (view === VIEW_RADAR_DECISIONS && flags?.FEATURE_RADAR_DECISIONS_REGISTER === false) {
    return VIEW_OFFICES;
  }

  if (Array.isArray(reportFlags)) {
    const rMap = Object.fromEntries(reportFlags.map((r) => [r.key, r.enabled]));
    const reportKeysByView = {
      [VIEW_REPORT_WPR]: "report-wpr",
      [VIEW_REPORT_HC_TREND]: "report-headcount-trend",
      [VIEW_REPORT_OPEN_ROLES]: "report-open-roles",
      [VIEW_REPORT_SPACE_FIT]: "report-space-fit",
      [VIEW_REPORT_COST_LEAKAGE]: "report-cost-leakage",
      [VIEW_REPORT_RTO_COMPLIANCE]: "report-rto-compliance",
      [VIEW_REPORT_FOOT_TRAFFIC]: "report-foot-traffic",
      [VIEW_REPORT_UTILIZATION]: "report-utilization",
      [VIEW_REPORT_DATA_COVERAGE]: "report-data-coverage",
    };
    const reportKey = reportKeysByView[view];
    if (reportKey && rMap[reportKey] !== true) return VIEW_REPORTS;
  }

  return view;
}

/**
 * Derive the shell header page label.
 * When on a location detail and the selected location is in the portfolio list,
 * returns the location's market name instead of the generic "Location" fallback.
 */
export function derivePageLabel({ view, selectedLocationId, portfolioLocations }) {
  if (view === VIEW_LOCATION_DETAIL && selectedLocationId) {
    const loc = (portfolioLocations || []).find((l) => l.id === selectedLocationId);
    if (loc && loc.market) return loc.market;
  }
  return PAGE_LABELS[view] || "";
}

/**
 * L7 — Filter-aware Next/Previous location nav derivation.
 * Reads persisted filter + sort state, applies the same pipeline as
 * PortfolioView, then positions the current location in the result.
 * Returns null neighbors at set boundaries and when the current location
 * is not in the filtered set.
 *
 * CALLER CONTRACT: `portfolioLocations` must be pre-ranked via
 * `rankSites(rawLocations)` before being passed here.
 */
export function deriveNavLocations(locationId, portfolioLocations, persistedState, searchTerm = "") {
  if (!locationId || !portfolioLocations || portfolioLocations.length === 0) {
    return { prevLocation: null, nextLocation: null };
  }
  let ordered;
  if (persistedState.sortMode === "column" && persistedState.listSortKey) {
    const filtered = applyAllFilters(portfolioLocations, persistedState.filters);
    ordered = applyColumnSort(filtered, persistedState.listSortKey, persistedState.listSortDir ?? 1);
  } else {
    ordered = applyAllFiltersAndSort(
      portfolioLocations,
      persistedState.filters,
      persistedState.sortKey,
    );
  }
  ordered = applySearchFilter(ordered, searchTerm);
  const idx = ordered.findIndex((loc) => loc.id === locationId);
  if (idx < 0) return { prevLocation: null, nextLocation: null };
  return {
    prevLocation: idx > 0               ? ordered[idx - 1] : null,
    nextLocation: idx < ordered.length - 1 ? ordered[idx + 1] : null,
  };
}

/**
 * NAV-PREVNEXT-RESPECT-FILTER-SCOPE — Resolve prev/next record from a
 * page-supplied ordered ID list.
 *
 * Used when the list page is mounted and has pushed its current filtered+sorted
 * ID sequence via `onNavListChange`.  Returns { prev, next } by looking up
 * adjacent IDs in `orderedIds` and resolving them against `records`.
 *
 * Returns { prev: null, next: null } when:
 *  - `currentId` is absent from `orderedIds` (out-of-scope record)
 *  - list is empty or null (deep-link / page not yet mounted)
 *  - at the boundary (prev=null at index 0; next=null at the end)
 *
 * @param {string|null}  currentId   - ID of the record currently open.
 * @param {string[]|null} orderedIds - Filtered+sorted ID list from the page.
 * @param {object[]}     records     - Full record list to resolve neighbors from.
 * @param {Function}     [pick]      - Optional transform applied to a resolved record.
 * @returns {{ prev: object|null, next: object|null }}
 */
export function resolveNavNeighbors(currentId, orderedIds, records, pick = (r) => r) {
  if (!currentId || !Array.isArray(orderedIds) || orderedIds.length === 0) {
    return { prev: null, next: null };
  }
  const idx = orderedIds.indexOf(currentId);
  if (idx < 0) return { prev: null, next: null };
  const resolve = (id) => {
    if (!id) return null;
    const r = records.find((rec) => rec.id === id);
    return r ? pick(r) : null;
  };
  return {
    prev: resolve(idx > 0 ? orderedIds[idx - 1] : null),
    next: resolve(idx < orderedIds.length - 1 ? orderedIds[idx + 1] : null),
  };
}

/**
 * LSE-LEASES-LIST-DETAIL-NAV-RESPECTS-LIST-SORT — Lease-list nav derivation.
 * Applies the same filter + sort pipeline as LeasesPage.
 * Returns { prevLease, nextLease } where each neighbor is { id, locationId, city } or null.
 */
export function deriveNavLeases(leaseId, allLeases, now, persistedFilters, persistedSort) {
  if (!leaseId || !Array.isArray(allLeases) || allLeases.length === 0) {
    return { prevLease: null, nextLease: null };
  }

  const filters      = persistedFilters  ?? DEFAULT_LEASE_FILTERS;
  const sortKey      = persistedSort?.sortKey      ?? "smart";
  const leaseSortKey = persistedSort?.leaseSortKey ?? null;
  const leaseSortDir = persistedSort?.leaseSortDir ?? 1;
  const sortMode     = persistedSort?.sortMode     ?? "dropdown";

  const filtered = applyLeaseFilters(allLeases, filters, now);

  let ordered;
  if (sortMode === "column" && leaseSortKey) {
    ordered = applyLeaseColumnSort(filtered, leaseSortKey, leaseSortDir);
  } else {
    ordered = applyLeaseSort(filtered, sortKey);
  }

  const idx = ordered.findIndex((l) => l.id === leaseId);
  if (idx < 0) return { prevLease: null, nextLease: null };

  const pick = (l) => (l ? { id: l.id, locationId: l.locationId, city: l.city } : null);
  return {
    prevLease: idx > 0               ? pick(ordered[idx - 1]) : null,
    nextLease: idx < ordered.length - 1 ? pick(ordered[idx + 1]) : null,
  };
}
